'use client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { ContentSuggestion } from '@//app/api/suggestions/combined';
import {
  InputSearch,
  InputSearchSuggestion,
  InputSearchSuggestionGroup,
  InputSearchSuggestionOption,
} from '@//components/InputSearch';
import useContentSuggestions from '@//data/suggestions/useContentSuggestions';
import useDebouncedValue from '@//hooks/useDebouncedValue';
import useSuggestionFoldoutManager from '@//hooks/useSuggestionFoldoutManager';

interface SearchBarProps {
  articlesPage: string;
  articlesSearchPage: string;
  initialQuery?: string;
}

const SearchBar = ({ articlesPage, articlesSearchPage, initialQuery = '' }: SearchBarProps) => {
  const router = useRouter();
  const params = useSearchParams();
  const t = useTranslations('pages.articles.articleList.searchBar');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(false);

  const { control, register, getValues } = useForm({
    defaultValues: { query: initialQuery },
  });
  const query = useWatch({ control, name: 'query' });
  const debouncedQuery = useDebouncedValue(query, 150);

  const contentSuggestionsQuery = useContentSuggestions(debouncedQuery);
  const contentSuggestions = contentSuggestionsQuery.data;
  const suggestions = contentSuggestions?.suggestions || [];

  const {
    activeIndex,
    activeSection,
    activeItem,
    sections,
    showDropdown,
    onFocus,
    goToNext,
    goToPrev,
    resetSuggestions,
  } = useSuggestionFoldoutManager<never, never, ContentSuggestion, never>({
    geoLocationSuggestions: [],
    vehicleSuggestions: [],
    articleSuggestions: suggestions,
    filterHistory: [],
    searchQuery: query,
    queryString: contentSuggestions?.queryString,
    clickOutsideRef: inputRef,
  });

  const onSuggestionClick = useCallback(
    (suggestion: ContentSuggestion) => {
      router.push(`${articlesPage}${suggestion.path}`);
      resetSuggestions();
    },
    [articlesPage, router, resetSuggestions],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        sections.some((section) => section.items.length > 0)
      ) {
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          goToNext();
        } else {
          goToPrev();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeItem && activeItem.type === 'article') {
          onSuggestionClick(activeItem.item as ContentSuggestion);
        } else {
          const currentQ = getValues('query');
          const queryParam = currentQ ? `q=${currentQ}` : '';
          setLoading(true);
          router.push(`${articlesSearchPage}?${queryParam}`);
          resetSuggestions();
        }
      }
    },
    [sections, goToNext, goToPrev, resetSuggestions, onSuggestionClick, activeItem],
  );

  useEffect(() => {
    setLoading(false);
  }, [params.toString()]);

  const loadingSuggestions =
    loading ||
    (!!query && (query !== contentSuggestions?.queryString || contentSuggestionsQuery.isLoading));

  return (
    <InputSearch
      ref={inputRef}
      id="article-search"
      label={t('label')}
      register={register('query')}
      autoComplete="off"
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      defaultValue={initialQuery}
      placeholder={t('placeholder')}
      suggestionExpanded={showDropdown}
      suggestionListId="article-search-suggestions-list"
      suggestionActiveOptionId={
        activeIndex > -1 ? `article-search-suggestions-option-${activeIndex}` : undefined
      }
      loading={loadingSuggestions}
      limitWidth
    >
      {showDropdown && (
        <InputSearchSuggestion id="article-search-suggestions-list">
          {(() => {
            let absIdx = 0;
            return sections.map(
              (section) =>
                section.items.length > 0 && (
                  <InputSearchSuggestionGroup
                    key={section.type}
                    id={`article-search-${section.type}-group`}
                    label={section.label}
                  >
                    {section.items.map((item) => {
                      const isActive = activeIndex === absIdx && activeSection === section.type;
                      const currentIdx = absIdx;
                      absIdx++;

                      if (section.type === 'article') {
                        const articleItem = item as ContentSuggestion;
                        return (
                          <InputSearchSuggestionOption
                            key={`article-search-suggestion-${currentIdx}`}
                            id={`article-search-suggestions-option-${currentIdx}`}
                            href={`${articlesPage}${articleItem.path}`}
                            onClick={() => onSuggestionClick(articleItem)}
                            title={articleItem.label}
                            isActive={isActive}
                          >
                            {articleItem.label}
                          </InputSearchSuggestionOption>
                        );
                      }
                      return null;
                    })}
                  </InputSearchSuggestionGroup>
                ),
            );
          })()}
        </InputSearchSuggestion>
      )}
    </InputSearch>
  );
};

export default SearchBar;
